import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminGuard from '../components/AdminGuard';
import AdminLayout from '../components/AdminLayout';
import { api } from '../../../api/client';

interface User {
  id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  authMethod: string;
  emailVerified: boolean;
  isAdmin: boolean;
  profileImage?: string;
  createdAt: string;
  updatedAt: string;
}

interface Timeline {
  id: string;
  name: string;
  isPublic: boolean;
  publicCode?: string;
  createdAt: string;
}

interface Device {
  id: string;
  platform: string;
  createdAt: string;
}

interface LiveActivity {
  id: string;
  timelineId: string;
  createdAt: string;
}

interface UserDetailData {
  user: User;
  ownedTimelines: Timeline[];
  subscribedTimelines: Timeline[];
  devices: Device[];
  liveActivities: LiveActivity[];
}

const AdminUserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get(`/admin/users/${id}`) as UserDetailData;
        setData(response);
      } catch (err) {
        const apiError = err as { message?: string };
        setError(apiError.message || 'Failed to load user');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <AdminGuard>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading...</div>
          </div>
        </AdminLayout>
      </AdminGuard>
    );
  }

  if (error || !data) {
    return (
      <AdminGuard>
        <AdminLayout>
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
            {error || 'User not found'}
          </div>
        </AdminLayout>
      </AdminGuard>
    );
  }

  const { user, ownedTimelines, subscribedTimelines, devices, liveActivities } = data;

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <Link to="/admin/users" className="text-blue-600 hover:text-blue-700 dark:text-blue-400">
              Users
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600 dark:text-gray-400">{user.email}</span>
          </div>

          {/* User Info Card */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No name'}
                  {user.isAdmin && (
                    <span className="ml-2 px-2 py-1 text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                      Admin
                    </span>
                  )}
                </h2>
                <p className="text-gray-500 dark:text-gray-400">{user.email}</p>
              </div>
              {user.profileImage && (
                <img
                  src={user.profileImage}
                  alt="Profile"
                  className="w-16 h-16 rounded-full"
                />
              )}
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Auth Method</p>
                <p className="font-medium text-gray-900 dark:text-white capitalize">{user.authMethod}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Email Verified</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {user.emailVerified ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Joined</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(user.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Last Updated</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(user.updatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Owned Timelines */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Owned Timelines ({ownedTimelines.length})
            </h3>
            {ownedTimelines.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No owned timelines</p>
            ) : (
              <div className="space-y-2">
                {ownedTimelines.map((timeline) => (
                  <div
                    key={timeline.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{timeline.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {timeline.isPublic ? `Public (${timeline.publicCode})` : 'Private'}
                      </p>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(timeline.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Subscribed Timelines */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Subscribed Timelines ({subscribedTimelines.length})
            </h3>
            {subscribedTimelines.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No subscribed timelines</p>
            ) : (
              <div className="space-y-2">
                {subscribedTimelines.map((timeline) => (
                  <div
                    key={timeline.id}
                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <p className="font-medium text-gray-900 dark:text-white">{timeline.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Devices */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Registered Devices ({devices.length})
            </h3>
            {devices.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No registered devices</p>
            ) : (
              <div className="space-y-2">
                {devices.map((device) => (
                  <div
                    key={device.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <span className="font-medium text-gray-900 dark:text-white capitalize">
                      {device.platform}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Registered {formatDate(device.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live Activities */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Active Live Activities ({liveActivities.length})
            </h3>
            {liveActivities.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No active Live Activities</p>
            ) : (
              <div className="space-y-2">
                {liveActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Timeline: {activity.timelineId}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Created {formatDate(activity.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* View Notifications for this User */}
          <div className="flex justify-end">
            <Link
              to={`/admin/notifications?userId=${id}`}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              View Notification History
            </Link>
          </div>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
};

export default AdminUserDetailPage;
